import { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import ButtonComponent from "../../../components/common/button/Button";
import Modal from "../../../components/common/modal/Modal";
import {
  viewUserVariantEndpoint,
  deleteUserVariantEndpoint,
  shareVariantEndpoint,
  shareVariantUsersEndpoint,
} from "../../../common/endpoints";
import { getVariantsList } from "../../../redux/actions/VariantView/GetVariantsListSlice";
import { deleteVariantAPI } from "../../../redux/actions/VariantView/DeleteVariantSlice";
import { shareVariantAPI } from "../../../redux/actions/VariantView/ShareVariantSlice";
import { getShareVariantUsers } from "../../../redux/actions/VariantView/ShareVariantUsersSlice";

export const ShowVariantViewModal = ({
  openShowVariantModal,
  handleCloseShowVariantModal,
  onClickSelectVariant,
  tableType,
}) => {
  const dispatch = useDispatch();
  const [variantsData, setVariantsData] = useState([]);
  const [sharePanel, setSharePanel] = useState(null); // { variant }
  const [shareUsers, setShareUsers] = useState([]);
  const [selectedShareUsers, setSelectedShareUsers] = useState([]);
  const [shareUsersLoading, setShareUsersLoading] = useState(false);
  const userEmail = localStorage.getItem("data");
  const rawRole = localStorage.getItem("appUserRole") || "";
  const GetVariantsListSlice = useSelector((state) => state.GetVariantsListSlice);

  const getVariantsData = async () => {
    try {
      const responseGetVariants = await dispatch(
        getVariantsList({
          dynamicEndpoint: `${viewUserVariantEndpoint}/${userEmail}`,
          params: {
            tableType,
            plant: localStorage.getItem("selectedLTPlantCode"),
          },
        })
      );

      if (responseGetVariants?.meta?.requestStatus === "fulfilled") {
        setVariantsData(responseGetVariants?.payload?.variants || []);
      } else {
        console.error("Failed to fetch data:", responseGetVariants?.error);
        toast.error(responseGetVariants?.payload?.errorMessage || "Failed to load variants", {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("API error:", error);
      toast.error("Failed to load variants", {
        autoClose: 2000,
      });
    }
  };

  useEffect(() => {
    if (openShowVariantModal) {
      getVariantsData();
    }
  }, [openShowVariantModal]);

  const onSelect = (variant) => {
    onClickSelectVariant?.(variant);
    handleCloseShowVariantModal?.();
  };

  const onOpenSharePanel = async (item) => {
    setSharePanel({ variant: item });
    setSelectedShareUsers([]);
    setShareUsersLoading(true);
    try {
      const response = await dispatch(
        getShareVariantUsers({
          dynamicEndpoint: `${shareVariantUsersEndpoint}?shareVariantRole=${encodeURIComponent(rawRole)}`,
        })
      );
      if (response?.meta?.requestStatus === "fulfilled") {
        setShareUsers(Array.isArray(response?.payload) ? response.payload : []);
      } else {
        setShareUsers([]);
      }
    } catch {
      setShareUsers([]);
    } finally {
      setShareUsersLoading(false);
    }
  };

  const onCloseSharePanel = () => {
    setSharePanel(null);
    setSelectedShareUsers([]);
    setShareUsers([]);
  };

  const onToggleShareUser = (user) => {
    setSelectedShareUsers((prev) => {
      const exists = prev.some((u) => u.email === user.email);
      return exists ? prev.filter((u) => u.email !== user.email) : [...prev, user];
    });
  };

  const onSubmitShare = async () => {
    if (!sharePanel?.variant || selectedShareUsers.length === 0) return;
    try {
      const response = await dispatch(
        shareVariantAPI({
          dynamicEndpoint: shareVariantEndpoint,
          data: {
            userEmail,
            userVariantMapSeqId: sharePanel.variant.userVariantMapSeqId,
            tableType,
            sharedUsers: selectedShareUsers.map((u) => ({ email: u.email, name: u.name })),
          },
        })
      );
      if (response?.meta?.requestStatus === "fulfilled") {
        toast.success(
          typeof response?.payload === "string"
            ? response.payload
            : "Variant shared successfully",
          { autoClose: 2000 }
        );
        onCloseSharePanel();
      } else {
        toast.error(response?.payload?.message || "Failed to share variant", { autoClose: 3000 });
      }
    } catch (error) {
      console.error("API error:", error);
    }
  };

  const onDelete = async (item) => {
    try {
      const response = await dispatch(
        deleteVariantAPI({
          dynamicEndpoint: `${deleteUserVariantEndpoint}/${item.userVariantMapSeqId}?userEmail=${encodeURIComponent(userEmail)}`,
        })
      );
      if (response?.meta?.requestStatus === "fulfilled") {
        toast.success(response?.payload || "Variant deleted", { autoClose: 2000 });
        getVariantsData();
      } else {
        toast.error(response?.payload?.message || "Failed to delete variant", { autoClose: 3000 });
      }
    } catch (error) {
      console.error("API error:", error);
    }
  };

  const isLoading = GetVariantsListSlice?.loading;
  const actionButtonStyles = { height: "auto", wordBreak: "normal" };

  return (
    <Modal
      className="showVariantModal_cls"
      open={openShowVariantModal}
      handleClose={handleCloseShowVariantModal}
      title="Show Variants"
      fullWidth={true}
      action={
        <div style={{ display: "flex", gap: 8 }}>
          <ButtonComponent
            label="Close"
            type="primary"
            maxWidth="90px"
            onClick={handleCloseShowVariantModal}
          />
        </div>
      }
      content={
        isLoading ? (
          <Box style={{ textAlign: "center", height: 100, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress color="inherit" style={{ width: "20px", height: "20px", marginRight: 30 }} />
          </Box>
        ) : sharePanel ? (
          <div>
            <div style={{ marginBottom: 12, fontWeight: 600 }}>
              Share &ldquo;{sharePanel.variant?.variantName}&rdquo; with users:
            </div>
            {shareUsersLoading ? (
              <Box style={{ textAlign: "center", height: 80, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CircularProgress color="inherit" style={{ width: "20px", height: "20px" }} />
              </Box>
            ) : shareUsers.length === 0 ? (
              <p style={{ color: "#666" }}>No users available to share with.</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 240, overflowY: "auto" }}>
                {shareUsers.map((user) => {
                  const checked = selectedShareUsers.some((u) => u.email === user.email);
                  return (
                    <label key={user.email} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => onToggleShareUser(user)}
                      />
                      <span>{user.name} &lt;{user.email}&gt;</span>
                    </label>
                  );
                })}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
              <ButtonComponent
                label="Back"
                type="primary"
                maxWidth="80px"
                onClick={onCloseSharePanel}
              />
              <ButtonComponent
                label="Share"
                type="secondary"
                bgColor="#1976d2"
                color="#ffffff"
                maxWidth="80px"
                onClick={onSubmitShare}
                disabled={selectedShareUsers.length === 0}
              />
            </div>
          </div>
        ) : (
          <div>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "600px" }}>
              <thead>
                <tr style={{ backgroundColor: "#f5f5f5" }}>
                  <th style={{ border: "1px solid #bcbcbc", padding: "8px", textAlign: "left", maxWidth: "200px" }}>Variant Name</th>
                  <th style={{ border: "1px solid #bcbcbc", padding: "8px", textAlign: "left", maxWidth: "350px" }}>Variant Description</th>
                  <th style={{ border: "1px solid #bcbcbc", padding: "8px", textAlign: "left", maxWidth: "130px" }}>Shared By</th>
                  <th style={{ border: "1px solid #bcbcbc", padding: "8px", textAlign: "left", maxWidth: "150px" }}>Created On</th>
                  <th style={{ border: "1px solid #bcbcbc", padding: "8px", textAlign: "left", maxWidth: "150px" }}>Modified On</th>
                  <th style={{ border: "1px solid #bcbcbc", padding: "8px", textAlign: "left" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {variantsData.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ border: "1px solid #bcbcbc", padding: "10px", textAlign: "center" }}>
                      No variants available
                    </td>
                  </tr>
                ) : (
                  variantsData.map((item) => (
                    <tr key={item?.userVariantMapSeqId}>
                      <td style={{ border: "1px solid #bcbcbc", padding: "8px", textAlign: "left", maxWidth: "200px", wordBreak: "break-word" }}>{item?.variantName}</td>
                      <td style={{ border: "1px solid #bcbcbc", padding: "8px", textAlign: "left", maxWidth: "350px", wordBreak: "break-word" }}>{item?.variantDescription}</td>
                      <td style={{ border: "1px solid #bcbcbc", padding: "8px", textAlign: "left", maxWidth: "130px", wordBreak: "break-word" }}>{item?.sharedBy || "-"}</td>
                      <td style={{ border: "1px solid #bcbcbc", padding: "8px", textAlign: "left", maxWidth: "150px" }}>{item?.createdOn}</td>
                      <td style={{ border: "1px solid #bcbcbc", padding: "8px", textAlign: "left", maxWidth: "150px" }}>{item?.modifiedOn}</td>
                      <td style={{ border: "1px solid #bcbcbc", padding: "8px", textAlign: "center" }}>
                        <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                          <ButtonComponent
                            label="Select"
                            type="secondary"
                            maxWidth="80px"
                            styles={actionButtonStyles}
                            onClick={() => onSelect(item)}
                          />
                          <ButtonComponent
                            label="Share"
                            type="secondary"
                            bgColor="#1976d2"
                            color="#ffffff"
                            maxWidth="80px"
                            styles={actionButtonStyles}
                            onClick={() => onOpenSharePanel(item)}
                          />
                          <ButtonComponent
                            label="Delete"
                            type="secondary"
                            bgColor="#e70d0d"
                            color="#ffffff"
                            maxWidth="80px"
                            styles={actionButtonStyles}
                            onClick={() => onDelete(item)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )
      }
    />
  );
};
